import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Checkbox, Drawer, Empty, Input, Modal, Select, message } from 'antd';
import { LogOut } from 'lucide-react';
import Layout from '../../Layout';
import { hrApi, useHr } from './hrStore';
import {
  LEAVE_TYPE, OFF_TASKS, branchById, daysBetween, empById, fmtD, isWorking, noticeShortfall, offTasksFor, positionById, requiredNotice,
  leaveBalance, settleDeadline, toDay, unreturnedAssets, type Offboarding as Off,
} from './hrLogic';
import { Badge, Chip, EmpCell, Notes, PageHeader, empUrl, fmtDateTime, useHrPerms } from './hrUi';
import { OffboardModal } from './hrModals';

type F = 'open' | 'done' | 'cancelled';

function OffDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const s = useHr();
  const navigate = useNavigate();
  const { me, canEdit } = useHrPerms();
  const [cancelNote, setCancelNote] = useState<string | null>(null);
  const today = toDay();
  const o = s.offboardings.find(x => x.id === id);
  const e = empById(s, o?.empId);
  if (!o || !e) return <Drawer open={!!id} onClose={onClose} title="Thủ tục nghỉ việc" />;
  const tasks = offTasksFor(s, o);
  const short = noticeShortfall(s, o);
  const need = requiredNotice(s, o.empId, o.type, o.noticeDate);
  const deadline = settleDeadline(o);
  const pending = unreturnedAssets(e);
  const leaveLeft = Math.max(0, leaveBalance(s, e, o.lastDay).remain);
  const run =(r: { ok: boolean; errors?: string[] }, ok: string) => {
    if (r.ok) { message.success(ok); return true; }
    Modal.error({ title: 'Chưa thực hiện được', content: r.errors?.join('\n') });
    return false;
  };
  return (
    <Drawer open={!!id} onClose={onClose} width={520} title={<div><div>Thủ tục nghỉ việc · {e.name}</div><div className="text-xs font-normal text-gray-500">{positionById(s, e.positionId)?.name} · {branchById(s, e.branchId)?.short}</div></div>}>
      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <div><div className="text-[11px] text-gray-400">Hình thức</div>{LEAVE_TYPE[o.type]}</div>
          <div><div className="text-[11px] text-gray-400">Lý do</div>{o.reason}</div>
          <div><div className="text-[11px] text-gray-400">Ngày báo / nhận đơn</div>{fmtD(o.noticeDate)}</div>
          <div><div className="text-[11px] text-gray-400">Ngày làm việc cuối</div>{fmtD(o.lastDay)}</div>
          <div><div className="text-[11px] text-gray-400">Hạn thanh toán</div><span className={o.status === 'open' && !o.tasks.settlement && deadline < today ? 'text-rose-600 font-medium' : ''}>{fmtD(deadline)}</span></div>
          <div><div className="text-[11px] text-gray-400">Báo trước theo HĐ</div>{need.label}</div>
        </div>
        {o.note && <div className="text-gray-600 bg-gray-50 dark:bg-gray-800/60 rounded-lg px-3 py-2">{o.note}</div>}
        <Notes warnings={short && o.status === 'open' ? [`Báo trước ${short.given} ngày, chưa đủ ${short.days} ngày — ngày nghỉ sớm nhất ${fmtD(short.minLast)} (trừ khi hai bên thỏa thuận)`] : []} />
        <div>
          <div className="font-semibold mb-2">Việc cần làm ({tasks.filter(t => o.tasks[t]).length}/{tasks.length})</div>
          <div className="space-y-2">
            {tasks.map(t => (
              <div key={t} className="flex items-start gap-2">
                <Checkbox disabled={!canEdit || o.status !== 'open'} checked={!!o.tasks[t]} onChange={x => { const r = hrApi.toggleOffTask(o.id, t, x.target.checked, me); if (!r.ok) message.warning(r.errors?.join(', ')); }} />
                <div className="min-w-0 flex-1">
                  <div>{OFF_TASKS[t]}</div>
                  {o.tasks[t] && <div className="text-[11px] text-gray-400">{fmtDateTime(o.tasks[t]!.at)} · {o.tasks[t]!.by}</div>}
                  {t === 'settlement' && leaveLeft > 0 && <div className="text-[11px] text-amber-700">Gồm tiền lương {leaveLeft} ngày phép năm chưa nghỉ</div>}
                  {t === 'assets' && pending.length > 0 && (
                    <div className="mt-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 space-y-0.5">
                      {pending.map(a => <div key={a.id}>• {a.name} <span className="text-amber-600">({a.code}, cấp {fmtD(a.issuedAt)})</span></div>)}
                      {canEdit && o.status === 'open' && (
                        <Button size="small" className="mt-1" onClick={() => run(hrApi.returnAssets(e.id, pending.map(a => a.id), today, me), `Đã thu hồi ${pending.length} tài sản`)}>Đã thu hồi đủ</Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        {o.noRehire && <Badge className="bg-rose-50 text-rose-700 border-rose-200">Không tái tuyển</Badge>}
        {canEdit && o.status === 'open' && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
            <Button type="primary" disabled={tasks.some(t => !o.tasks[t]) || o.lastDay > today} onClick={() => run(hrApi.completeOffboarding(o.id, me), 'Đã hoàn tất thủ tục')}>Hoàn tất thủ tục</Button>
            {o.lastDay >= today && <Button onClick={() => setCancelNote('')}>Nhân sự rút đơn / hủy</Button>}
            <Button type="text" onClick={() => navigate(empUrl(e.id))}>Hồ sơ</Button>
          </div>
        )}
        {cancelNote !== null && (
          <div className="space-y-2">
            <Input placeholder="Lý do hủy (VD: đã thỏa thuận ở lại)" value={cancelNote} onChange={x => setCancelNote(x.target.value)} />
            <div className="flex gap-2">
              <Button danger type="primary" onClick={() => { if (run(hrApi.cancelOffboarding(o.id, me, cancelNote), 'Đã hủy thủ tục')) setCancelNote(null); }}>Xác nhận hủy</Button>
              <Button onClick={() => setCancelNote(null)}>Thôi</Button>
            </div>
          </div>
        )}
        <div>
          <div className="font-semibold mb-1">Lịch sử</div>
          {[...o.log].reverse().map((l, i) => <div key={i} className="text-xs"><span className="text-gray-400">{fmtDateTime(l.at)} · {l.by}</span> — {l.text}</div>)}
        </div>
      </div>
    </Drawer>
  );
}

/** Nghỉ việc: thời hạn báo trước, checklist bàn giao, báo giảm BHXH, hạn thanh toán 14 ngày */
export default function Offboarding() {
  const s = useHr();
  const [params, setParams] = useSearchParams();
  const { canEdit } = useHrPerms();
  const today = toDay();
  const f = (params.get('f') as F) || 'open';
  const id = params.get('id');
  const [pick, setPick] = useState<string | null>(null);
  const [empFor, setEmpFor] = useState<string | null>(null);
  const go = (p: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const list = s.offboardings.filter(o => o.status === f).sort((a, b) => (f === 'open' ? a.lastDay.localeCompare(b.lastDay) : b.lastDay.localeCompare(a.lastDay)));
  const status = (o: Off) => {
    const tasks = offTasksFor(s, o);
    const done = tasks.filter(t => o.tasks[t]).length;
    const deadline = settleDeadline(o);
    if (o.status !== 'open') return { done, total: tasks.length, text: o.status === 'done' ? 'Đã hoàn tất' : 'Đã hủy', cls: 'text-gray-400' };
    if (!o.tasks.settlement && deadline < today) return { done, total: tasks.length, text: `Quá hạn thanh toán ${daysBetween(deadline, today)} ngày`, cls: 'text-rose-600 font-medium' };
    if (o.lastDay < today) return { done, total: tasks.length, text: `Đã nghỉ ${daysBetween(o.lastDay, today)} ngày · hạn thanh toán ${fmtD(deadline)}`, cls: 'text-amber-600' };
    return { done, total: tasks.length, text: `Còn ${daysBetween(today, o.lastDay)} ngày làm việc`, cls: 'text-sky-600' };
  };

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Nghỉ việc" desc="Báo trước theo loại HĐ · bàn giao, thu hồi tài sản · báo giảm BHXH · thanh toán trong 14 ngày"
          extra={canEdit && <Button type="primary" icon={<LogOut size={14} />} onClick={() => setPick('')}>Lập thủ tục nghỉ việc</Button>} />
        <div className="flex flex-wrap gap-1.5">
          {([['open', 'Đang xử lý'], ['done', 'Đã hoàn tất'], ['cancelled', 'Đã hủy']] as [F, string][]).map(([k, l]) => (
            <Chip key={k} active={f === k} onClick={() => go({ f: k === 'open' ? null : k })}>{l} · {s.offboardings.filter(o => o.status === k).length}</Chip>
          ))}
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm divide-y divide-gray-100 dark:divide-gray-800">
          {list.length === 0 && <Empty className="py-10" image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có thủ tục" />}
          {list.map(o => {
            const e = empById(s, o.empId);
            const st = status(o);
            return (
              <div key={o.id} role="button" tabIndex={0} onClick={() => go({ id: o.id })} onKeyDown={ev => { if (ev.key === 'Enter') go({ id: o.id }); }} className="cursor-pointer w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/40 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <div className="md:w-[280px] shrink-0"><EmpCell s={s} e={e} /></div>
                <div className="flex-1 min-w-0 text-sm">
                  <div>{LEAVE_TYPE[o.type]} · <span className="text-gray-500">{o.reason}</span></div>
                  <div className="text-xs text-gray-400">Báo {fmtD(o.noticeDate)} · nghỉ {fmtD(o.lastDay)}{noticeShortfall(s, o) && o.status === 'open' ? ' · báo trước chưa đủ' : ''}</div>
                </div>
                <div className="md:w-[260px] shrink-0">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${(st.done / st.total) * 100}%` }} /></div>
                    <span className="text-xs text-gray-500">{st.done}/{st.total}</span>
                  </div>
                  <div className={`text-[11px] mt-0.5 ${st.cls}`}>{st.text}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <OffDrawer id={id} onClose={() => go({ id: null })} />
      {pick !== null && (
        <Modal open title="Chọn nhân sự nghỉ việc" onCancel={() => setPick(null)} okText="Tiếp tục" cancelText="Đóng" okButtonProps={{ disabled: !pick }}
          onOk={() => { setEmpFor(pick); setPick(null); }}>
          <Select showSearch className="w-full" placeholder="Tìm nhân sự" optionFilterProp="label" value={pick || undefined} onChange={setPick}
            options={s.employees.filter(e => isWorking(e, today) && !e.leftDate).map(e => ({ value: e.id, label: `${e.name} · ${e.code} · ${positionById(s, e.positionId)?.name} · ${branchById(s, e.branchId)?.short}` }))} />
        </Modal>
      )}
      {empFor && <OffboardModal empId={empFor} onClose={() => setEmpFor(null)} />}
    </Layout>
  );
}
